import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  AlertTriangle, ArrowLeft, ArrowUpRight, CheckCircle2, Circle, CircleCheck, Info, Loader2, RefreshCw, ShieldCheck, XCircle,
} from "lucide-react";
import { useAuth } from "../lib/AuthContext";
import { useNoIndex } from "../lib/useNoIndex";
import { EmSwash } from "../components/EmSwash";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const client = axios.create({ baseURL: API, withCredentials: true });
client.interceptors.request.use((cfg) => {
  const t = localStorage.getItem("mra_jwt");
  if (t) cfg.headers = { ...(cfg.headers || {}), Authorization: `Bearer ${t}` };
  return cfg;
});

// Front-end build flags the server can't see; evaluated in the browser.
const clientItems = () => {
  const open = String(process.env.REACT_APP_STUDENT_CAMPAIGNS_OPEN || "").toLowerCase() === "true";
  const donationsLive = String(process.env.REACT_APP_DONATIONS_LIVE || "").toLowerCase() === "true";
  const host = (() => { try { return new URL(process.env.REACT_APP_BACKEND_URL).hostname; } catch { return ""; } })();
  const prod = host.endsWith("myrichaunt.org");
  return [
    { key: "fe_backend_url", label: "Front end talks to", status: prod ? "ok" : "info", detail: host || "unknown", fix: prod ? null : "Preview build. Production must point at myrichaunt.org." },
    { key: "fe_donations_live", label: "Donations switch (REACT_APP_DONATIONS_LIVE)", status: donationsLive ? "ok" : (prod ? "fail" : "info"), detail: donationsLive ? "LIVE — donate buttons open real Stripe Checkout." : "OFF — donate buttons show the coming-soon dialog.", fix: donationsLive ? null : "Set REACT_APP_DONATIONS_LIVE=true in the production build." },
    { key: "fe_students_open", label: "Prototype notices (REACT_APP_STUDENT_CAMPAIGNS_OPEN)", status: "info", detail: open ? "OPEN — 'Real students, live now.'" : "PROTOTYPE — 'Sample campaigns, not live.' notices show.", fix: open ? null : "Flip to true on launch day, together with the backend switch." },
  ];
};

const STATUS = {
  ok:     { Icon: CheckCircle2,  cls: "text-grass",     label: "Ready" },
  warn:   { Icon: AlertTriangle, cls: "text-tangerine", label: "Needs attention" },
  fail:   { Icon: XCircle,       cls: "text-scarlet",   label: "Blocking" },
  info:   { Icon: Info,          cls: "text-cobalt",    label: "For your information" },
};

const Row = ({ it, onToggle }) => {
  const manual = it.status === "manual";
  const meta = STATUS[it.status] || STATUS.info;
  const Icon = manual ? (it.done ? CircleCheck : Circle) : meta.Icon;
  return (
    <li data-testid={`launch-item-${it.key}`} data-status={manual ? (it.done ? "done" : "todo") : it.status}
      className="grid grid-cols-[28px_1fr] gap-3 border-t border-border/70 py-4 first:border-t-0">
      {manual ? (
        <button type="button" role="checkbox" aria-checked={!!it.done} onClick={() => onToggle(it)} data-testid={`launch-toggle-${it.key}`}
          className={`mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full transition-colors ${it.done ? "text-grass" : "text-ink/30 hover:text-ink"}`}>
          <Icon className="h-6 w-6" strokeWidth={2} />
        </button>
      ) : (
        <Icon className={`mt-0.5 h-6 w-6 ${meta.cls}`} strokeWidth={2} aria-label={meta.label} />
      )}
      <div className="min-w-0">
        <p className={`text-sm font-semibold ${manual && it.done ? "text-olive line-through decoration-olive/50" : "text-ink"}`}>{it.label}</p>
        {!manual && <p className="mt-0.5 break-words text-sm text-olive">{it.detail}</p>}
        {manual && it.done && <p className="mt-0.5 text-xs text-olive">Done {it.done_at ? new Date(it.done_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : ""}{it.done_by ? ` · ${it.done_by}` : ""}</p>}
        {it.fix && !(manual && it.done) && (
          <p className="mt-1.5 rounded-md bg-tangerine/[0.08] px-3 py-2 text-xs leading-relaxed text-ink"><span className="font-semibold text-tangerine">How to fix: </span>{it.fix}</p>
        )}
        {it.link && (
          <Link to={it.link} className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-cobalt underline underline-offset-2">Open {it.link} <ArrowUpRight className="h-3 w-3" /></Link>
        )}
      </div>
    </li>
  );
};

const Stat = ({ n, label, cls, testid }) => (
  <div data-testid={testid} className="rounded-xl border border-border bg-bone px-4 py-3">
    <p className={`font-serif text-3xl leading-none ${cls}`}>{n}</p>
    <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-olive">{label}</p>
  </div>
);

export default function AdminLaunch() {
  useNoIndex();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true); setError("");
    try { setData((await client.get("/admin/launch-checklist")).data); }
    catch (e) { setError(e?.response?.data?.detail || "Couldn't load the checklist."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) navigate("/login", { replace: true, state: { from: "/admin/launch" } });
    else if (!user.is_admin) navigate("/dashboard", { replace: true });
    else refresh();
  }, [authLoading, user, navigate, refresh]);

  const toggle = async (it) => {
    try {
      const { data: row } = await client.patch(`/admin/launch-checklist/${it.key}`, { done: !it.done });
      setData((d) => ({ ...d, groups: d.groups.map((g) => ({ ...g, items: g.items.map((x) => (x.key === row.key ? { ...x, ...row } : x)) })),
        summary: { ...d.summary, manual_done: d.summary.manual_done + (row.done ? 1 : -1) } }));
      toast.success(row.done ? "Checked off." : "Reopened.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save."); }
  };

  const fe = useMemo(clientItems, []);
  const feCounts = fe.reduce((a, x) => ({ ...a, [x.status]: (a[x.status] || 0) + 1 }), {});
  const s = data?.summary;
  const blocking = (s?.fail || 0) + (feCounts.fail || 0);
  const attention = (s?.warn || 0) + (feCounts.warn || 0);

  return (
    <main data-testid="admin-launch-page" className="min-h-screen bg-sand text-ink">
      <div className="mx-auto max-w-5xl px-6 pb-24 pt-10 lg:px-8">
        <Link to="/admin" className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-olive hover:text-ink"><ArrowLeft className="h-3.5 w-3.5" /> Admin</Link>
        <div className="mt-6 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.22em] text-tangerine"><ShieldCheck className="h-4 w-4" /> Launch day</span>
            <h1 className="mt-3 font-serif text-5xl leading-[0.95] tracking-tight text-ink lg:text-6xl">Go-live <EmSwash>checklist.</EmSwash></h1>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-olive">
              Every switch, secret and setting in one place. Green is ready, amber needs a look, red blocks launch. Circles are your own steps; tick them as you go.
            </p>
            {data && (
              <p className="mt-3 text-xs text-olive" data-testid="launch-meta">
                Checked {new Date(data.checked_at).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })} · environment: <span className={`font-semibold ${data.environment === "production" ? "text-grass" : "text-cobalt"}`} data-testid="launch-environment">{data.environment}</span> · {data.origin}
              </p>
            )}
          </div>
          <button type="button" onClick={refresh} disabled={loading} data-testid="launch-recheck"
            className="inline-flex items-center gap-2 self-start rounded-full bg-ink px-5 py-3 text-sm font-semibold text-sand transition-colors hover:bg-forest disabled:opacity-60 lg:self-auto">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Re-check everything
          </button>
        </div>

        {error && <p role="alert" data-testid="launch-error" className="mt-8 rounded-xl border border-scarlet/30 bg-scarlet/5 px-4 py-3 text-sm text-scarlet">{error}</p>}

        {data && (
          <>
            <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-5" data-testid="launch-summary">
              <Stat n={(s.ok || 0) + (feCounts.ok || 0)} label="Ready" cls="text-grass" testid="launch-stat-ok" />
              <Stat n={attention} label="Needs a look" cls="text-tangerine" testid="launch-stat-warn" />
              <Stat n={blocking} label="Blocking" cls={blocking ? "text-scarlet" : "text-ink"} testid="launch-stat-fail" />
              <Stat n={(s.info || 0) + (feCounts.info || 0)} label="FYI" cls="text-cobalt" testid="launch-stat-info" />
              <Stat n={`${s.manual_done}/${s.manual_total}`} label="Your steps" cls="text-ink" testid="launch-stat-manual" />
            </div>

            <div className="mt-10 space-y-8">
              {data.groups.map((g) => (
                <section key={g.key} data-testid={`launch-group-${g.key}`} className="rounded-2xl border border-border bg-bone p-6">
                  <h2 className="font-serif text-2xl text-ink">{g.title}</h2>
                  <ul className="mt-4">{g.items.map((it) => <Row key={it.key} it={it} onToggle={toggle} />)}</ul>
                </section>
              ))}
              <section data-testid="launch-group-frontend" className="rounded-2xl border border-border bg-bone p-6">
                <h2 className="font-serif text-2xl text-ink">Front-end build switches</h2>
                <p className="mt-1 text-xs text-olive">Read from this browser's build, so they reflect the site you're looking at right now.</p>
                <ul className="mt-4">{fe.map((it) => <Row key={it.key} it={it} onToggle={() => {}} />)}</ul>
              </section>
            </div>
          </>
        )}
        {loading && !data && <p className="mt-10 flex items-center gap-2 text-sm text-olive"><Loader2 className="h-4 w-4 animate-spin" /> Running every check…</p>}
      </div>
    </main>
  );
}
